<script setup lang="ts">
import type { LayoutEmptyState } from '#layers/listing/types/layout';
import type { CalendarRange } from './build-calendar-range-filter';
import type { CalendarItemEvent } from './calendar-events';
import { Temporal } from 'temporal-polyfill';
import { computed, nextTick, onMounted, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserLocale } from '#layers/base/public';
import LayoutDefaultEmptyState from '#layers/listing/components/Layout/LayoutDefaultEmptyState.vue';
import { CALENDAR_DAY_LABEL_FORMAT, CalendarViewModes } from '#layers/listing/constants/calendar';
import { isDateInRange } from './build-calendar-range-filter';
import { assignEventsToDays } from './calendar-events';
import { formatCalendarTitle } from './format-calendar-title';

const {
	range,
	focusedDate,
	events,
	timeZone,
	today,
	emptyState,
} = defineProps<{
	range: CalendarRange;
	focusedDate: Temporal.PlainDate;
	events: readonly CalendarItemEvent[];
	timeZone: string;
	today: Temporal.PlainDate;
	isSettled: boolean;
	emptyState?: LayoutEmptyState;
}>();

const emit = defineEmits<{
	open: [calendarEvent: CalendarItemEvent];
	emptyStateAction: [];
}>();

defineSlots<{
	event: (props: { calendarEvent: CalendarItemEvent }) => unknown;
}>();

interface ListDay {
	key: string;
	date: Temporal.PlainDate;
	isToday: boolean;
	isPast: boolean;
	label: string;
	weekdayLabel: string;
	events: CalendarItemEvent[];
}

const { t } = useI18n();
const { formatLocale } = useUserLocale();

const days = computed<ListDay[]>(() =>
	[...assignEventsToDays({ events, range })]
		.map(([key, dayEvents]) => toListDay(Temporal.PlainDate.from(key), dayEvents))
		.toSorted((a, b) => Temporal.PlainDate.compare(a.date, b.date)),
);

const emptyStateProps = computed<LayoutEmptyState>(() => ({
	...emptyState,
	title: formatCalendarTitle(CalendarViewModes.List, range, formatLocale.value),
	description: emptyState?.description ?? t('layout.empty.description'),
}));

const scroller = useTemplateRef<HTMLElement>('scroller');

function toListDay(date: Temporal.PlainDate, dayEvents: CalendarItemEvent[]): ListDay {
	const formattedDate = date.toLocaleString(formatLocale.value, CALENDAR_DAY_LABEL_FORMAT);
	const count = dayEvents.length;

	return {
		key: date.toString(),
		date,
		isToday: date.equals(today),
		isPast: Temporal.PlainDate.compare(date, today) < 0,
		label: t('layout.calendar.day_label', { date: formattedDate, count }, count),
		weekdayLabel: date.toLocaleString(formatLocale.value, { weekday: 'short' }),
		events: sortByStartOnDay(dayEvents, date),
	};
}

// Puts date items and items that started on an earlier day first, then sorts the rest by start time.
function sortByStartOnDay(dayEvents: CalendarItemEvent[], date: Temporal.PlainDate) {
	const dayStart = date.toZonedDateTime(timeZone);

	function getStartOnDay(event: CalendarItemEvent) {
		const start = event.start instanceof Temporal.ZonedDateTime ? event.start : dayStart;
		return Temporal.ZonedDateTime.compare(start, dayStart) < 0 ? dayStart : start;
	}

	return dayEvents.toSorted((a, b) => Temporal.ZonedDateTime.compare(getStartOnDay(a), getStartOnDay(b)));
}

// Keeps a date in view while the month's days load, until the user scrolls away.
let pending: { date: Temporal.PlainDate; top?: number } | undefined;

async function scrollToPendingDate() {
	await nextTick();

	const container = scroller.value;
	if (!pending || !container) return;

	const { date, top } = pending;

	if (!isDateInRange(date, range) || (top !== undefined && container.scrollTop !== top)) {
		pending = undefined;
		return;
	}

	const target = days.value.find((day) => Temporal.PlainDate.compare(day.date, date) >= 0) ?? days.value.at(-1);
	if (!target) return;

	const group = container.querySelector<HTMLElement>(`[data-list-day="${target.key}"]`);
	container.scrollTo({ top: group?.offsetTop ?? 0, behavior: 'instant' });
	pending.top = container.scrollTop;
}

function scrollToDate(date: Temporal.PlainDate) {
	pending = { date };
	return scrollToPendingDate();
}

watch(days, () => scrollToPendingDate());

// Previous and Next open a month at its top.
watch(() => range, (range) => {
	if (pending && isDateInRange(pending.date, range)) return;

	pending = undefined;
	scroller.value?.scrollTo({ top: 0, behavior: 'instant' });
});

onMounted(() => scrollToDate(focusedDate));

defineExpose({ scrollToDate });
</script>

<template>
	<div
		ref="scroller"
		class="relative flex flex-1 flex-col overflow-y-auto pb-6"
	>
		<section
			v-for="day in days"
			:key="day.key"
			:data-list-day="day.key"
			class="flex items-start gap-3 border-b border-(--cal-line) px-6 py-2"
		>
			<h3
				class="sr-only"
				:aria-current="day.isToday ? 'date' : undefined"
			>
				{{ day.label }}
			</h3>
			<div
				aria-hidden="true"
				:data-past="day.isPast || undefined"
				:data-today="day.isToday || undefined"
				class="
					group sticky top-2 flex size-11 shrink-0 flex-col items-center justify-center rounded-md bg-(--cal-block)
					tabular-nums ring-1 ring-(--cal-block-ring) ring-inset
					data-past:bg-muted data-past:ring-default
					data-today:bg-primary data-today:ring-0
				"
			>
				<span class="text-xs text-(--cal-block-time) group-data-past:text-dimmed group-data-today:text-inverted">
					{{ day.weekdayLabel }}
				</span>
				<span
					class="text-base/5 font-semibold text-highlighted group-data-past:text-muted group-data-today:text-inverted"
				>
					{{ day.date.day }}
				</span>
			</div>

			<div class="flex min-w-0 flex-1 flex-col">
				<button
					v-for="calendarEvent in day.events"
					:key="calendarEvent.id"
					type="button"
					class="
						flex min-h-9 w-full items-center rounded-md px-2 py-1.5 text-start
						hover:bg-elevated
						focus:outline-none
						focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset
					"
					@click="emit('open', calendarEvent)"
				>
					<slot
						name="event"
						:calendar-event
					/>
				</button>
			</div>
		</section>

		<LayoutDefaultEmptyState
			v-if="isSettled && days.length === 0"
			:empty-state="emptyStateProps"
			@empty-state-action="emit('emptyStateAction')"
		/>
	</div>
</template>
